'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import {
	Carousel,
	CarouselControls,
	CarouselItem,
	CarouselItemNav,
	CarouselItems,
	CarouselNextButton,
	CarouselPreviousButton,
} from '@/components/elementary/carousel/Carousel'
import { BannerCard } from '@/components/shared/BannerCard'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/styles'
import type { ServerRenderedEntry } from '@/types/helpers'
import { getColsValue } from '@/utils/colsMapping'

export function CardsBannerContent({
	cards,
	showMoreAfterCount,
	bgClassName,
	disableBannerLayout,
	columns,
}: {
	readonly cards?: ServerRenderedEntry<Data.Component<'shared.banner-card'>>[] | null
	readonly showMoreAfterCount?: number | null
	readonly bgClassName?: string | null
	readonly disableBannerLayout?: boolean | null
	readonly columns?: Data.Component<'enums.columns'> | null
}) {
	const t = useTranslations('buttons')
	const sliceAfter = showMoreAfterCount ?? cards?.length ?? 0
	const [showMore, setShowMore] = useState(false)

	if (!Array.isArray(cards) || cards.length === 0) {
		return null
	}

	const cardBackgroundColorVariant = disableBannerLayout ? bgClassName : 'bg-neutral-light-100'
	const showCarousel = cards.length > 1

	return (
		<>
			{/* Mobile/Tablet: Carousel (only when more than 1 card) */}
			{showCarousel && (
				<div className="lg:hidden pb-14">
					<Carousel className="relative">
						<CarouselItems className="rounded-xl">
							{cards.map((card) => (
								<CarouselItem key={`carousel-${card.id}`} className="rounded-xl">
									<BannerCard card={card} cardBackgroundColorVariant={cardBackgroundColorVariant} />
								</CarouselItem>
							))}
						</CarouselItems>

						<CarouselControls className="absolute w-full justify-between -bottom-12 rounded-xl py-2 px-4 gap-7">
							<CarouselPreviousButton className="shadow-none bg-transparent p-0" />
							<CarouselItemNav activeColor="neutral-light-600" />
							<CarouselNextButton className="shadow-none bg-transparent p-0" />
						</CarouselControls>
					</Carousel>
				</div>
			)}

			{/* Mobile: Single card (no carousel needed) */}
			{!showCarousel && (
				<div className="lg:hidden">
					{cards.map((card) => (
						<BannerCard
							key={`mobile-${card.id}`}
							card={card}
							cardBackgroundColorVariant={cardBackgroundColorVariant}
						/>
					))}
				</div>
			)}

			{/* Desktop: Grid layout */}
			<div className={cn('hidden lg:grid gap-3 lg:gap-4', getColsValue(columns?.column))}>
				{cards.slice(0, showMore ? cards.length : sliceAfter).map((card) => (
					<BannerCard
						key={`grid-${card.id}`}
						card={card}
						cardBackgroundColorVariant={cardBackgroundColorVariant}
					/>
				))}
			</div>

			{cards.length > sliceAfter && (
				<div className="hidden lg:flex w-full justify-center items-baseline">
					<Button variant="primary" size="large" onClick={() => setShowMore(!showMore)}>
						{showMore ? t('showLess') : t('showMore')}
					</Button>
				</div>
			)}
		</>
	)
}

export default CardsBannerContent
